import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import BranchAssignmentField, { useBranchAssignment } from '../components/BranchAssignmentField';
import { branchQuerySuffix } from '../components/OverviewCommandPanel';
import { SUCCESS_THEN_REDIRECT_MS } from '../utils/submitFeedback';
import {
  AUTOMOTIVE_BRANDS,
  AUTOMOTIVE_COMMON_MODELS,
  AUTOMOTIVE_STOCK_CATEGORIES,
  AUTOMOTIVE_UNITS,
  AUTOMOTIVE_VEHICLE_TYPES,
} from '../constants/automotiveStockCategories';
import './AutomotiveAddProductPage.css';

function normalizeText(v) {
  return String(v || '').trim();
}

function toOption(value) {
  const raw = typeof value === 'string' ? { label: value, value } : (value || {});
  const text = normalizeText(raw.value || raw.label || raw.name);
  if (!text) return null;
  return {
    label: text,
    value: text,
    id: raw.id ?? null,
    phone: normalizeText(raw.phone || ''),
    region: normalizeText(raw.region || ''),
    remote: Boolean(raw.remote || raw.id),
  };
}

function mergeUniqueOptions(items) {
  const map = new Map();
  (items || []).forEach((item) => {
    const opt = toOption(item);
    if (!opt) return;
    const key = opt.value.toLowerCase();
    if (!map.has(key)) {
      map.set(key, opt);
      return;
    }
    const prev = map.get(key);
    map.set(key, {
      ...prev,
      label: prev.label || opt.label,
      value: prev.value || opt.value,
      id: opt.id ?? prev.id ?? null,
      phone: opt.phone || prev.phone || '',
      region: opt.region || prev.region || '',
      remote: Boolean(prev.remote || opt.remote || opt.id),
    });
  });
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const AutomotiveAddProductPage = ({ onNavigate, initialSection = 'record' }) => {
  const { addProduct, updateProduct, products, inventoryLoading } = useProducts();
  const { user } = useAuth();
  const branchAssignment = useBranchAssignment(user);
  const [branchProducts, setBranchProducts] = useState([]);
  const [branchProductsLoading, setBranchProductsLoading] = useState(false);
  const tenantId = user?.tenant?.id || user?.tenantId || 'auto-default';
  const storagePrefix = useMemo(() => `cbonline.auto-presets.${tenantId}.`, [tenantId]);

  const [name, setName] = useState('');
  const [partCode, setPartCode] = useState('');
  const [oemNumber, setOemNumber] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [compatibleModels, setCompatibleModels] = useState('');
  const [unit, setUnit] = useState('pcs');
  const [lowStockLimit, setLowStockLimit] = useState('10');

  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [categoryOptions, setCategoryOptions] = useState(() => mergeUniqueOptions(AUTOMOTIVE_STOCK_CATEGORIES));
  const [unitOptions, setUnitOptions] = useState(() => mergeUniqueOptions(AUTOMOTIVE_UNITS));
  const [brandOptions, setBrandOptions] = useState(() => mergeUniqueOptions(AUTOMOTIVE_BRANDS));

  const [newCategory, setNewCategory] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [searchCategories, setSearchCategories] = useState('');
  const [searchUnits, setSearchUnits] = useState('');
  const [searchBrands, setSearchBrands] = useState('');

  const [openingProductId, setOpeningProductId] = useState('');
  const [openingQty, setOpeningQty] = useState('');
  const [openingBuyPrice, setOpeningBuyPrice] = useState('');
  const [openingSellPrice, setOpeningSellPrice] = useState('');
  const [openingBusy, setOpeningBusy] = useState(false);
  const [openingPanelOpen, setOpeningPanelOpen] = useState(initialSection === 'opening-stock');

  const partNameRef = useRef(null);
  const categoryDraftRef = useRef(null);
  const brandDraftRef = useRef(null);
  const unitDraftRef = useRef(null);
  const openingQtyRef = useRef(null);

  const busy = phase === 'loading' || openingBusy;
  const isOpeningOnly = initialSection === 'opening-stock';
  const showOpeningSection = isOpeningOnly;
  const showRecordForm = !isOpeningOnly;

  useEffect(() => {
    if (initialSection !== 'record') {
      setManageOpen(true);
    }
    if (initialSection === 'opening-stock') {
      setOpeningPanelOpen(true);
    }
    const timer = window.setTimeout(() => {
      if (initialSection === 'record') partNameRef.current?.focus();
      if (initialSection === 'categories') categoryDraftRef.current?.focus();
      if (initialSection === 'brands') brandDraftRef.current?.focus();
      if (initialSection === 'units') unitDraftRef.current?.focus();
      if (initialSection === 'opening-stock') openingQtyRef.current?.focus();
    }, 40);
    return () => window.clearTimeout(timer);
  }, [initialSection]);

  const openingProducts = useMemo(() => {
    const source = branchAssignment.isOwner ? branchProducts : products;
    if (!Array.isArray(source)) return [];
    return [...source]
      .filter((p) => String(p?.approvalStatus || 'approved') === 'approved')
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' }));
  }, [branchAssignment.isOwner, branchProducts, products]);

  useEffect(() => {
    if (!branchAssignment.ready) {
      setBranchProducts([]);
      return undefined;
    }
    if (!branchAssignment.isOwner) {
      setBranchProducts([]);
      return undefined;
    }

    let mounted = true;
    setBranchProductsLoading(true);
    (async () => {
      try {
        const data = await apiJson(`/products${branchQuerySuffix(branchAssignment.branchId)}`);
        if (!mounted) return;
        setBranchProducts(Array.isArray(data.products) ? data.products : []);
      } catch {
        if (mounted) setBranchProducts([]);
      } finally {
        if (mounted) setBranchProductsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [branchAssignment.isOwner, branchAssignment.ready, branchAssignment.branchId]);

  useEffect(() => {
    setOpeningProductId('');
  }, [branchAssignment.branchId]);

  const selectedOpeningProduct = useMemo(
    () => openingProducts.find((p) => String(p.id) === String(openingProductId)) || null,
    [openingProducts, openingProductId]
  );

  useEffect(() => {
    if (!selectedOpeningProduct) {
      setOpeningBuyPrice('');
      setOpeningSellPrice('');
      return;
    }
    const buy = Number(selectedOpeningProduct.purchasingPrice) || 0;
    const sell = Number(selectedOpeningProduct.sellingPrice) || 0;
    setOpeningBuyPrice(buy > 0 ? String(buy) : '');
    setOpeningSellPrice(sell > 0 ? String(sell) : '');
  }, [selectedOpeningProduct]);

  const defaultCategories = useMemo(
    () => new Set(AUTOMOTIVE_STOCK_CATEGORIES.map((x) => normalizeText(x).toLowerCase())),
    []
  );
  const defaultUnits = useMemo(
    () => new Set(AUTOMOTIVE_UNITS.map((x) => normalizeText(x).toLowerCase())),
    []
  );
  const defaultBrands = useMemo(
    () => new Set(AUTOMOTIVE_BRANDS.map((x) => normalizeText(x).toLowerCase())),
    []
  );

  useEffect(() => {
    try {
      const catRaw = window.localStorage.getItem(`${storagePrefix}categories`);
      const unitRaw = window.localStorage.getItem(`${storagePrefix}units`);
      const brandRaw = window.localStorage.getItem(`${storagePrefix}brands`);
      const removedCatRaw = window.localStorage.getItem(`${storagePrefix}categories.removed`);
      const removedUnitRaw = window.localStorage.getItem(`${storagePrefix}units.removed`);
      const removedBrandRaw = window.localStorage.getItem(`${storagePrefix}brands.removed`);

      const extraCats = catRaw ? JSON.parse(catRaw) : [];
      const extraUnits = unitRaw ? JSON.parse(unitRaw) : [];
      const extraBrands = brandRaw ? JSON.parse(brandRaw) : [];
      const removedCats = new Set((removedCatRaw ? JSON.parse(removedCatRaw) : []).map((x) => normalizeText(x).toLowerCase()));
      const removedUnits = new Set((removedUnitRaw ? JSON.parse(removedUnitRaw) : []).map((x) => normalizeText(x).toLowerCase()));
      const removedBrands = new Set((removedBrandRaw ? JSON.parse(removedBrandRaw) : []).map((x) => normalizeText(x).toLowerCase()));
      const defaultCats = AUTOMOTIVE_STOCK_CATEGORIES.filter((x) => !removedCats.has(normalizeText(x).toLowerCase()));
      const defaultUnitsList = AUTOMOTIVE_UNITS.filter((x) => !removedUnits.has(normalizeText(x).toLowerCase()));
      const defaultBrandsList = AUTOMOTIVE_BRANDS.filter((x) => !removedBrands.has(normalizeText(x).toLowerCase()));

      setCategoryOptions(mergeUniqueOptions([...defaultCats, ...extraCats]));
      setUnitOptions(mergeUniqueOptions([...defaultUnitsList, ...extraUnits]));
      setBrandOptions(mergeUniqueOptions([...defaultBrandsList, ...extraBrands]));
    } catch {
      setCategoryOptions(mergeUniqueOptions(AUTOMOTIVE_STOCK_CATEGORIES));
      setUnitOptions(mergeUniqueOptions(AUTOMOTIVE_UNITS));
      setBrandOptions(mergeUniqueOptions(AUTOMOTIVE_BRANDS));
    }
  }, [storagePrefix]);

  const persistOptions = (key, options, defaults = new Set()) => {
    try {
      const values = (options || [])
        .map((o) => normalizeText(o.value))
        .filter((v) => v && !defaults.has(v.toLowerCase()));
      window.localStorage.setItem(`${storagePrefix}${key}`, JSON.stringify(values));
    } catch {
      // Ignore quota or storage errors.
    }
  };

  const addPreset = (draft, options, setOptions, setValue, setDraft, keyName, successText, defaults = new Set()) => {
    const text = normalizeText(draft);
    if (!text) return;
    const merged = mergeUniqueOptions([...options, text]);
    setOptions(merged);
    persistOptions(keyName, merged, defaults);
    setValue(text);
    setDraft('');
    setHint(successText);
  };

  const removePreset = (value, options, setOptions, setValue, keyName, defaults = new Set()) => {
    const key = normalizeText(value).toLowerCase();
    if (!key || defaults.has(key)) return;
    const next = options.filter((o) => normalizeText(o.value).toLowerCase() !== key);
    setOptions(next);
    persistOptions(keyName, next, defaults);
    setValue((prev) => (normalizeText(prev).toLowerCase() === key ? '' : prev));
    setHint('Preset removed.');
  };

  const editPreset = (value, options, setOptions, setValue, setDraft, keyName, defaults = new Set()) => {
    const key = normalizeText(value).toLowerCase();
    if (!key || defaults.has(key)) return;
    const next = options.filter((o) => normalizeText(o.value).toLowerCase() !== key);
    setOptions(next);
    persistOptions(keyName, next, defaults);
    setValue((prev) => (normalizeText(prev).toLowerCase() === key ? '' : prev));
    setDraft(value);
    setHint('Edit preset then click Add.');
  };

  const customCategories = useMemo(
    () => categoryOptions.filter((o) => !defaultCategories.has(normalizeText(o.value).toLowerCase())),
    [categoryOptions, defaultCategories]
  );
  const customUnits = useMemo(
    () => unitOptions.filter((o) => !defaultUnits.has(normalizeText(o.value).toLowerCase())),
    [unitOptions, defaultUnits]
  );
  const customBrands = useMemo(
    () => brandOptions.filter((o) => !defaultBrands.has(normalizeText(o.value).toLowerCase())),
    [brandOptions, defaultBrands]
  );

  const filteredCategories = useMemo(
    () => customCategories.filter((o) => normalizeText(o.label).toLowerCase().includes(normalizeText(searchCategories).toLowerCase())),
    [customCategories, searchCategories]
  );
  const filteredUnits = useMemo(
    () => customUnits.filter((o) => normalizeText(o.label).toLowerCase().includes(normalizeText(searchUnits).toLowerCase())),
    [customUnits, searchUnits]
  );
  const filteredBrands = useMemo(
    () => customBrands.filter((o) => normalizeText(o.label).toLowerCase().includes(normalizeText(searchBrands).toLowerCase())),
    [customBrands, searchBrands]
  );
  const clearFormFields = () => {
    setName('');
    setPartCode('');
    setOemNumber('');
    setVehicleType('');
    setCategory('');
    setBrand('');
    setCompatibleModels('');
    setUnit('pcs');
    setLowStockLimit('10');
  };

  const reset = () => {
    clearFormFields();
    setError('');
    setHint('');
    setSuccessMessage('');
  };

  const submitOpeningStock = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setHint('');
    setSuccessMessage('');
    if (!branchAssignment.ready) {
      setError('Select a branch for this opening stock.');
      return;
    }
    const product = selectedOpeningProduct;
    if (!product) {
      setError('Select a product for opening stock.');
      return;
    }
    const qty = Number(openingQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Enter a valid opening stock quantity above 0.');
      return;
    }
    const buy = parseFloat(openingBuyPrice);
    const sell = parseFloat(openingSellPrice);
    if (!Number.isFinite(buy) || buy < 0) {
      setError('Enter a valid cost price for this stock.');
      return;
    }
    if (!Number.isFinite(sell) || sell < 0) {
      setError('Enter a valid selling price for this stock.');
      return;
    }
    if (sell < buy) {
      setError('Selling price must be greater than or equal to cost price.');
      return;
    }

    setOpeningBusy(true);
    try {
      const currentQty = Number(product.quantity) || 0;
      const nextQty = Math.round((currentQty + qty + Number.EPSILON) * 100) / 100;
      await updateProduct(product.id, {
        name: product.name,
        category: product.category || '',
        quantity: nextQty,
        purchasingPrice: buy,
        sellingPrice: sell,
        lowStockLimit: Number(product.lowStockLimit) || 0,
        unit: product.unit || 'pcs',
        expiryDate: product.expiryDate || null,
        batchLot: product.batchLot || null,
        barcode: product.barcode || null,
        partCode: product.partCode || null,
        oemNumber: product.oemNumber || null,
        vehicleType: product.vehicleType || null,
        compatibleModels: product.compatibleModels || null,
        brandName: product.brandName || null,
        supplierName: product.supplierName || null,
        branchId: Number(branchAssignment.branchId),
      });
      setOpeningQty('');
      setOpeningBuyPrice('');
      setOpeningSellPrice('');
      setOpeningProductId('');
      const unitLabel = product.unit || 'pcs';
      setSuccessMessage(
        `✓ Opening stock saved. "${product.name}" now has ${nextQty} ${unitLabel} in inventory and is ready to sell. Redirecting to Inventory…`
      );
      setTimeout(() => {
        if (typeof onNavigate === 'function') onNavigate('auto-inventory');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setError(err?.message || 'Could not record opening stock.');
    } finally {
      setOpeningBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    setSuccessMessage('');

    if (!branchAssignment.ready) {
      setError('Select a branch for this part catalog entry.');
      return;
    }
    if (!name.trim()) {
      setError('Enter part name.');
      return;
    }
    if (!category) {
      setError('Select category.');
      return;
    }

    try {
      setPhase('loading');
      const displayName = brand ? `${name.trim()} (${brand})` : name.trim();
      await addProduct(
        {
          name: displayName,
          category,
          quantity: 0,
          purchasingPrice: 0,
          sellingPrice: 0,
          lowStockLimit: parseInt(lowStockLimit || '10', 10),
          unit,
          partCode: partCode.trim() || null,
          oemNumber: oemNumber.trim() || null,
          vehicleType: vehicleType || null,
          compatibleModels: compatibleModels.trim() || null,
          brandName: brand || null,
          supplierName: null,
          branchId: Number(branchAssignment.branchId),
        },
        user
      );
      clearFormFields();
      setSuccessMessage(`✓ "${displayName}" saved to your parts catalog. Redirecting to Parts list…`);
      setTimeout(() => {
        if (typeof onNavigate === 'function') onNavigate('stock-list');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setError(err?.message || 'Could not save part.');
    } finally {
      setPhase('idle');
    }
  };

  return (
    <div className="automotive-add-product-page page-bleed">
      <header className="automotive-add-header">
        <div>
          <h1 className="automotive-add-title">{isOpeningOnly ? 'Opening Stock' : 'Add Spare Part'}</h1>
          <p className="automotive-add-lede">
            {isOpeningOnly
              ? 'Add physical stock and prices for parts already registered in your catalog. Stocked parts appear in Inventory and can be sold at POS.'
              : 'Register spare parts you sell in your shop. Stock, suppliers, and prices are handled in opening stock or purchases.'}
          </p>
        </div>
        {typeof onNavigate === 'function' && (
          <button
            type="button"
            className="automotive-add-back"
            onClick={() => onNavigate(isOpeningOnly ? 'auto-inventory' : 'stock-list')}
            disabled={busy}
          >
            ← Back
          </button>
        )}
      </header>

      <div className="automotive-add-container">
        {error ? (
          <div className="automotive-add-feedback automotive-add-alert automotive-add-alert--error" role="alert">
            {error}
          </div>
        ) : null}

        {successMessage ? (
          <div className="automotive-add-feedback automotive-add-alert automotive-add-alert--success" role="status">
            {successMessage}
          </div>
        ) : null}

        {hint ? (
          <div className="automotive-add-feedback automotive-add-alert automotive-add-alert--hint" role="status">
            {hint}
          </div>
        ) : null}

        {showOpeningSection ? (
        <section className="automotive-add-section automotive-add-opening">
          <div className="automotive-add-manage-head">
            <h2 className="automotive-add-section-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              Opening Stock
            </h2>
          </div>

          <form className="automotive-add-grid" onSubmit={submitOpeningStock}>
              <BranchAssignmentField
                user={user}
                assignment={branchAssignment}
                label="Branch (stock goes here)"
                hint="Opening stock is added to inventory at this branch."
                className="automotive-add-field"
              />

              <label className="automotive-add-field">
                <span className="automotive-add-label">Registered product</span>
                <select
                  className="automotive-add-input automotive-add-select"
                  value={openingProductId}
                  onChange={(e) => setOpeningProductId(e.target.value)}
                  disabled={busy || inventoryLoading || branchProductsLoading || !branchAssignment.ready}
                  required
                >
                  <option value="">{inventoryLoading || branchProductsLoading ? 'Loading products…' : 'Select product…'}</option>
                  {openingProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="automotive-add-hint">Linked to product records you already registered.</span>
              </label>

              <div className="automotive-add-opening-meta">
                <div>
                  <strong>Category:</strong> {selectedOpeningProduct?.category || '—'}
                </div>
                <div>
                  <strong>Unit:</strong> {selectedOpeningProduct?.unit || '—'}
                </div>
                <div>
                  <strong>Current stock:</strong> {selectedOpeningProduct ? `${Number(selectedOpeningProduct.quantity) || 0} ${selectedOpeningProduct.unit || 'pcs'}` : '—'}
                </div>
              </div>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Opening quantity to add *</span>
                <input
                  ref={openingQtyRef}
                  className="automotive-add-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={openingQty}
                  onChange={(e) => setOpeningQty(e.target.value)}
                  placeholder="e.g. 24"
                  disabled={busy}
                  required
                />
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Cost per unit (TZS) *</span>
                <input
                  className="automotive-add-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={openingBuyPrice}
                  onChange={(e) => setOpeningBuyPrice(e.target.value)}
                  placeholder="0.00"
                  disabled={busy}
                  required
                />
                <span className="automotive-add-hint">What you paid for this stock</span>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Selling price (TZS) *</span>
                <input
                  className="automotive-add-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={openingSellPrice}
                  onChange={(e) => setOpeningSellPrice(e.target.value)}
                  placeholder="0.00"
                  disabled={busy}
                  required
                />
                <span className="automotive-add-hint">Counter price when selling this part</span>
              </label>

              <div className="automotive-add-actions" style={{ marginTop: 0 }}>
                <button type="submit" className="automotive-add-submit" disabled={busy || !openingProductId || !branchAssignment.ready}>
                  {openingBusy ? 'Recording…' : '+ Record Opening Stock'}
                </button>
              </div>
            </form>
        </section>
        ) : null}

        {showRecordForm ? (
        <form className="automotive-add-form" onSubmit={handleSubmit} noValidate>
          {/* LEFT SECTION: Part Info */}
          <section className="automotive-add-section">
            <h2 className="automotive-add-section-title">Part Details</h2>

            <div className="automotive-add-grid">
              <BranchAssignmentField
                user={user}
                assignment={branchAssignment}
                label="Branch"
                hint="This part is registered for this branch catalog."
                className="automotive-add-field"
              />

              <label className="automotive-add-field">
                <span className="automotive-add-label">Part Name *</span>
                <input
                  ref={partNameRef}
                  className="automotive-add-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Brake pad set (front)"
                  required
                  disabled={busy}
                  autoComplete="off"
                />
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Part Code / SKU</span>
                <input
                  className="automotive-add-input"
                  type="text"
                  value={partCode}
                  onChange={(e) => setPartCode(e.target.value)}
                  placeholder="e.g. BRK-001-F"
                  disabled={busy}
                />
                <span className="automotive-add-hint">Optional internal SKU or shelf code</span>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">OEM / Part Number</span>
                <input
                  className="automotive-add-input"
                  type="text"
                  value={oemNumber}
                  onChange={(e) => setOemNumber(e.target.value)}
                  placeholder="e.g. 04465-0K290"
                  disabled={busy}
                />
                <span className="automotive-add-hint">Original or manufacturer part number</span>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Vehicle Type</span>
                <select
                  className="automotive-add-input automotive-add-select"
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select type...</option>
                  {AUTOMOTIVE_VEHICLE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Category *</span>
                <select
                  className="automotive-add-input automotive-add-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  required
                  disabled={busy}
                >
                  <option value="">Select category…</option>
                  {categoryOptions.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <div className="automotive-add-inline-add">
                  <input
                    ref={categoryDraftRef}
                    className="automotive-add-input"
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add new category"
                    disabled={busy}
                  />
                  <button
                    type="button"
                    className="automotive-add-inline-btn"
                    onClick={() =>
                      addPreset(
                        newCategory,
                        categoryOptions,
                        setCategoryOptions,
                        setCategory,
                        setNewCategory,
                        'categories',
                        'Category added.',
                        defaultCategories
                      )
                    }
                    disabled={busy}
                  >
                    Add
                  </button>
                </div>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Brand</span>
                <select
                  className="automotive-add-input automotive-add-select"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select brand…</option>
                  {brandOptions.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
                <div className="automotive-add-inline-add">
                  <input
                    ref={brandDraftRef}
                    className="automotive-add-input"
                    type="text"
                    value={newBrand}
                    onChange={(e) => setNewBrand(e.target.value)}
                    placeholder="Add new brand"
                    disabled={busy}
                  />
                  <button
                    type="button"
                    className="automotive-add-inline-btn"
                    onClick={() =>
                      addPreset(
                        newBrand,
                        brandOptions,
                        setBrandOptions,
                        setBrand,
                        setNewBrand,
                        'brands',
                        'Brand added.',
                        defaultBrands
                      )
                    }
                    disabled={busy}
                  >
                    Add
                  </button>
                </div>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Compatible Models</span>
                <input
                  className="automotive-add-input"
                  type="text"
                  value={compatibleModels}
                  onChange={(e) => setCompatibleModels(e.target.value)}
                  list="automotive-compatible-models"
                  placeholder="e.g. Toyota IST, Vitz, Corolla 2010-2020"
                  disabled={busy}
                />
                <datalist id="automotive-compatible-models">
                  {AUTOMOTIVE_COMMON_MODELS.map((model) => (
                    <option key={model} value={model} />
                  ))}
                </datalist>
                <span className="automotive-add-hint">Helps staff find parts by gari/pikipiki model.</span>
              </label>
            </div>
          </section>

          {/* RIGHT SECTION: Stock & Pricing */}
          <section className="automotive-add-section">
            <h2 className="automotive-add-section-title">Stock & Pricing</h2>

            <div className="automotive-add-grid">
              <label className="automotive-add-field">
                <span className="automotive-add-label">Unit *</span>
                <select
                  className="automotive-add-input automotive-add-select"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  required
                  disabled={busy}
                >
                  {unitOptions.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
                <div className="automotive-add-inline-add">
                  <input
                    ref={unitDraftRef}
                    className="automotive-add-input"
                    type="text"
                    value={newUnit}
                    onChange={(e) => setNewUnit(e.target.value)}
                    placeholder="Add new unit"
                    disabled={busy}
                  />
                  <button
                    type="button"
                    className="automotive-add-inline-btn"
                    onClick={() =>
                      addPreset(
                        newUnit,
                        unitOptions,
                        setUnitOptions,
                        setUnit,
                        setNewUnit,
                        'units',
                        'Unit added.',
                        defaultUnits
                      )
                    }
                    disabled={busy}
                  >
                    Add
                  </button>
                </div>
              </label>

              <label className="automotive-add-field">
                <span className="automotive-add-label">Low Stock Alert</span>
                <input
                  className="automotive-add-input"
                  type="number"
                  min="1"
                  value={lowStockLimit}
                  onChange={(e) => setLowStockLimit(e.target.value)}
                  placeholder="10"
                  disabled={busy}
                />
                <span className="automotive-add-hint">Alert when stock drops below this</span>
              </label>
            </div>
          </section>

          {/* ACTIONS */}
          <section className="automotive-add-manage">
            <div className="automotive-add-manage-head">
              <h3 className="automotive-add-manage-title">Manage Custom Presets</h3>
              <button
                type="button"
                className="automotive-add-manage-toggle"
                onClick={() => setManageOpen((v) => !v)}
                disabled={busy}
              >
                {manageOpen ? 'Hide' : 'Show'}
              </button>
            </div>

            {manageOpen ? (
              <div className="automotive-add-manage-grid">
                <div className="automotive-add-manage-group">
                  <div className="automotive-add-manage-group-title">Categories</div>
                  <input
                    className="automotive-add-manage-search"
                    type="search"
                    placeholder="Search categories"
                    value={searchCategories}
                    onChange={(e) => setSearchCategories(e.target.value)}
                  />
                  {filteredCategories.length ? filteredCategories.map((item) => (
                    <div className="automotive-add-manage-item" key={`cat-${item.value}`}>
                      <span>{item.label}</span>
                      <div className="automotive-add-manage-actions">
                        <button
                          type="button"
                          onClick={() => editPreset(item.value, categoryOptions, setCategoryOptions, setCategory, setNewCategory, 'categories', defaultCategories)}
                          disabled={busy}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removePreset(item.value, categoryOptions, setCategoryOptions, setCategory, 'categories', defaultCategories)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )) : <p className="automotive-add-manage-empty">No matching custom categories.</p>}
                </div>

                <div className="automotive-add-manage-group">
                  <div className="automotive-add-manage-group-title">Units</div>
                  <input
                    className="automotive-add-manage-search"
                    type="search"
                    placeholder="Search units"
                    value={searchUnits}
                    onChange={(e) => setSearchUnits(e.target.value)}
                  />
                  {filteredUnits.length ? filteredUnits.map((item) => (
                    <div className="automotive-add-manage-item" key={`unit-${item.value}`}>
                      <span>{item.label}</span>
                      <div className="automotive-add-manage-actions">
                        <button
                          type="button"
                          onClick={() => editPreset(item.value, unitOptions, setUnitOptions, setUnit, setNewUnit, 'units', defaultUnits)}
                          disabled={busy}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removePreset(item.value, unitOptions, setUnitOptions, setUnit, 'units', defaultUnits)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )) : <p className="automotive-add-manage-empty">No matching custom units.</p>}
                </div>

                <div className="automotive-add-manage-group">
                  <div className="automotive-add-manage-group-title">Brands</div>
                  <input
                    className="automotive-add-manage-search"
                    type="search"
                    placeholder="Search brands"
                    value={searchBrands}
                    onChange={(e) => setSearchBrands(e.target.value)}
                  />
                  {filteredBrands.length ? filteredBrands.map((item) => (
                    <div className="automotive-add-manage-item" key={`brand-${item.value}`}>
                      <span>{item.label}</span>
                      <div className="automotive-add-manage-actions">
                        <button
                          type="button"
                          onClick={() => editPreset(item.value, brandOptions, setBrandOptions, setBrand, setNewBrand, 'brands', defaultBrands)}
                          disabled={busy}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removePreset(item.value, brandOptions, setBrandOptions, setBrand, 'brands', defaultBrands)}
                          disabled={busy}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )) : <p className="automotive-add-manage-empty">No matching custom brands.</p>}
                </div>

              </div>
            ) : null}
          </section>

          {/* ACTIONS */}
          <div className="automotive-add-actions">
            {typeof onNavigate === 'function' && (
              <button
                type="button"
                className="automotive-add-secondary"
                onClick={() => onNavigate('stock-list')}
                disabled={busy}
              >
                Cancel
              </button>
            )}
            <button type="submit" className="automotive-add-submit" disabled={busy}>
              {busy ? (
                <>
                  <span className="automotive-add-spinner" aria-hidden />
                  Saving…
                </>
              ) : (
                '+ Save Part'
              )}
            </button>
          </div>
        </form>
        ) : null}
      </div>
    </div>
  );
};

export default AutomotiveAddProductPage;
